/*
Flowbite's current modal implementation does not like being forcibly yanked out of the DOM.
The symptom is that modals stop working entirely until a full page refresh. This happens 
frequently for us as we do things like partial page updates with turbo streams.

The implemention here is a simplified copy of the Flowbite modal source code. Original version
is at: https://github.com/themesberg/flowbite/blob/main/src/components/modal/index.ts
The two key changes, beyond simplification, are:
1) Buildup & teardown of the modal happen in the Stimulus lifecycle callbacks.
2) When destroying the backdrop, it's not _assumed_ that element(s) matching [modal-backdrop]
are going to exist in the DOM.

This is meant to be a "short term" fix while we either seek a Stimulus-native modal component
or wait for Flowbite to improve their Turbo compatibility.
*/

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["close", "cancel"];

  static values = {
    // Set to false to disable closing the modal on hitting ESC or clicking on the backdrop
    closeable: { type: Boolean, default: true },   
  }  

  connect() {
    this._targetEl = this.element;
    this._isHidden = true;
    this._backdropEl = null;
    this._initialized = false;
    this._options = {
      closable: this.closeableValue,
      backdrop: "dynamic",
      backdropClasses: "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40"
    };

    if (this.hasCloseTarget) {
      this.closeTarget.addEventListener("click", () => {
        this.hide();
      });
    }

    if (this.hasCancelTarget) {
      this.cancelTarget.addEventListener("click", () => {
          this.hide();
      });
    }

    this.init();
    this.show();
  }

  disconnect() {
    this.hide();
    this.destroy();
  }

  init() {
    if (this._targetEl && !this._initialized) {
      this._getPlacementClasses().map((c) => {
          this._targetEl.classList.add(c);
      });
      this._initialized = true;
    }
  }

  destroy() {
      if (this._initialized) {
          this._destroyBackdropEl();
          this._initialized = false;
      }
  }

  _createBackdrop() {
    if (this._isHidden) {
      const backdropEl = document.createElement('div');
      backdropEl.setAttribute('modal-backdrop', '');
      backdropEl.classList.add(
        ...this._options.backdropClasses.split(' ')
      );
      document.querySelector('body').append(backdropEl);
      this._backdropEl = backdropEl;
    }
  }

  _destroyBackdropEl() {
    if (!this._isHidden) {
      document.querySelectorAll('[modal-backdrop]').forEach((el) => { el.remove(); })
    }
  }

  _setupModalCloseEventListeners() {
    if (this._options.backdrop === 'dynamic') {
      this._clickOutsideEventListener = (ev) => {
          this._handleOutsideClick(ev.target);
      };
      this._targetEl.addEventListener(
          'click',
          this._clickOutsideEventListener,
          true
      );
    }

    this._keydownEventListener = (ev) => {
        if (ev.key === 'Escape') {
            this.hide();
        }
    };
    document.body.addEventListener(
        'keydown',
        this._keydownEventListener,
        true
    );
  }

  _removeModalCloseEventListeners() {
    if (this._options.backdrop === 'dynamic') {
        this._targetEl.removeEventListener(
            'click',
            this._clickOutsideEventListener,
            true
        );
    }
    document.body.removeEventListener(
        'keydown',
        this._keydownEventListener,
        true
    );
  }

  _handleOutsideClick(target) {
    if (
        target === this._targetEl ||
        (target === this._backdropEl && !this._isHidden)
    ) {
        this.hide();
    }
  }

  _getPlacementClasses() {
    return ['justify-center', 'items-center'];
  }  

  show() {
    if (this._isHidden) {
        this._targetEl.classList.add('flex');
        this._targetEl.classList.remove('hidden');
        this._targetEl.setAttribute('aria-modal', 'true');
        this._targetEl.setAttribute('role', 'dialog');
        this._targetEl.removeAttribute('aria-hidden');
        this._createBackdrop();
        this._isHidden = false;

        // Add keyboard event listener to the document
        if (this._options.closable) {
            this._setupModalCloseEventListeners();
        }

        // prevent body scroll
        document.body.classList.add('overflow-hidden');
    }
  }

  hide() {
    if (!this._isHidden) {
        this._targetEl.classList.add('hidden');
        this._targetEl.classList.remove('flex');
        this._targetEl.setAttribute('aria-hidden', 'true');
        this._targetEl.removeAttribute('aria-modal');
        this._targetEl.removeAttribute('role');
        this._destroyBackdropEl();
        this._isHidden = true;

        // re-apply body scroll
        document.body.classList.remove('overflow-hidden');

        if (this._options.closable) {
          this._removeModalCloseEventListeners();
        }
    }
  }  
}